import Loader from 'reusable/mobx-helpers/Loader'
import { makeAutoObservable } from 'mobx'
import { ProposalDto } from '@gap/backend/app-services/proposal/models/ProposalDto'
import ProposalsEditPaymentStore from '../ProposalsEditPaymentStore'
import { ServiceRateTypeEnum, serviceRateTypeEnums } from '@gap/backend/app-services/services/models/ServiceRateTypesDto'
import { notNull } from '@gap/utils/utils'
import { getDefaultValuesPerRateType } from 'features/proposals/proposalsUtils'
import { formHandler, FormHandlerConfig } from 'reusable/mobx-store-utils/form-handlers/new/form-handler'
import { AutocompleteOption } from 'reusable/mui/auto-complete/shared/AutocompleteOption'
import { ProposalStep3ServiceAgreementTypeEnum } from '@gap/backend/app-services/proposal/models/ProposalStep3Dto'
import { defaultServiceAgreementType, defaultServicePrice, defaultServiceUnits } from 'features/proposals/proposalsConstants'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'

interface ProposalsEditPaymentServiceBlockStoreProps {
   proposalEditPaymentStore: ProposalsEditPaymentStore
   rateType: ServiceRateTypeEnum
   step3ServiceId: string
}

export default class ProposalsEditPaymentServiceBlockStore {
   loader = new Loader(false)
   proposal: ProposalDto
   sp

   fields = {
      servicePrice: defaultServicePrice,
      serviceAgreementType: defaultServiceAgreementType,
      startingPriceRate: 'Per month' as ServiceRateTypeEnum,
      serviceUnits: defaultServiceUnits
   }

   constructor(sp: ProposalsEditPaymentServiceBlockStoreProps) {
      makeAutoObservable(this)
      this.sp = sp

      this.fields.startingPriceRate = this.sp.rateType
      this.fields.servicePrice = this.step3Service?.servicePrice || defaultServicePrice
      this.fields.serviceAgreementType = this.step3Service?.serviceAgreementType || defaultServiceAgreementType
      this.fields.serviceUnits = this.step3Service?.serviceUnits || defaultServiceUnits
   }

   get stepData() {
      return notNull(this.sp.proposalEditPaymentStore.getStepByRateType(this.sp.rateType))
   }

   get step3Service() {
      return this.stepData.services.find(({ serviceId }) => serviceId === this.sp.step3ServiceId)
   }

   get step2Service() {
      return notNull(notNull(this.sp.proposalEditPaymentStore.proposal.step2).find(s => s.serviceId === this.step3Service?.serviceId))
   }

   get formHandler() {
      return formHandler(this.fields, {
         servicePrice: { type: 'number', min: 0, maxDecimalPlaces: 0, maxIntegerLength: 6, onValueChange: this.updateProposal },
         serviceUnits: { type: 'number', onValueChange: this.updateProposal }
      } satisfies FormHandlerConfig)
   }

   get serviceRateOptions() {
      return serviceRateTypeEnums.map(service => ({ id: service, label: service })) as AutocompleteOption[]
   }

   get showServiceUnits() {
      return ['Per session', 'Per unit'].includes(this.sp.rateType)
   }

   updateProposal = async () => {
      notNull(this.step3Service).servicePrice = Number(this.fields.servicePrice)
      notNull(this.step3Service).serviceAgreementType = this.fields.serviceAgreementType
      if (this.showServiceUnits) {
         notNull(this.step3Service).serviceUnits = Number(this.fields.serviceUnits)
      }
      await this.sp.proposalEditPaymentStore.update()
   }

   onNewPriceType = async (event: React.MouseEvent<HTMLElement, MouseEvent>, newValue: ProposalStep3ServiceAgreementTypeEnum) => {
      if (newValue) {
         this.fields.serviceAgreementType = newValue
         await this.updateProposal()
      }
   }

   onNewRateType = async (newValue: any) => {
      this.fields.startingPriceRate = newValue
      const service = JSON.parse(JSON.stringify(this.step3Service))

      // Adding to new step
      const step = this.sp.proposalEditPaymentStore.step.find(({ serviceRateType }) => serviceRateType === this.fields.startingPriceRate)
      if (step) {
         step.services.push(service)
      } else {
         this.sp.proposalEditPaymentStore.step.push({
            services: [service],
            ...getDefaultValuesPerRateType(this.fields.startingPriceRate)
         })
      }

      // Remove from current step
      this.stepData.services = this.stepData.services.filter(service => service.serviceId !== this.sp.step3ServiceId)
      await this.sp.proposalEditPaymentStore.update()
   }
}

export const { Provider: ProposalsEditPaymentServiceBlockStoreProvider, useProvidedStore: useProposalsEditPaymentServiceBlockStore } = createStoreContext(ProposalsEditPaymentServiceBlockStore)
